/**
 * KGAgentProgress
 *
 * Displays KG import progress inline on agent cards/rows.
 * Shows progress bar when an import is active, or status badge when not.
 */

import React from 'react';
import { Agent } from '../../../types';
import { useTaskManager } from '../../../contexts/TaskManagerContext';
import { useAdminTranslation } from '../../../modules/i18n';
import { ArrowPathIcon, EyeIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';

interface KGAgentProgressProps {
  agent: Agent;
  onViewDetails?: (jobId: string) => void;
}

const KGAgentProgress: React.FC<KGAgentProgressProps> = ({ agent, onViewDetails }) => {
  const { t } = useAdminTranslation();
  const { getTasksByNamespace } = useTaskManager();

  const kg = agent.knowledgeGraph;
  const workflow = agent.workflow;
  const namespace = kg?.namespace;

  // Find active import for this agent's namespace
  const tasks = namespace ? getTasksByNamespace(namespace) : [];
  const activeImport = tasks.find((task) => task.taskType === 'kg_import' && task.status === 'RUNNING');
  const failedImport = tasks.find((task) => task.taskType === 'kg_import' && task.status === 'FAILED');

  // If there's an active import, show progress
  if (activeImport) {
    const progress = activeImport.progressPercentage || 0;
    const currentStep = activeImport.progressMessage || t('content.agents.workflowProgress.running');

    return (
      <div className='flex flex-col gap-1.5'>
        <div className='flex items-center gap-2'>
          <ArrowPathIcon className='h-4 w-4 animate-spin text-blue-500' />
          <span className='text-xs text-gray-600 dark:text-dark-text-secondary'>{currentStep}</span>
        </div>
        <div className='flex items-center gap-2'>
          <div className='h-1.5 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-dark-background-tertiary'>
            <div
              className='h-full rounded-full bg-blue-500 transition-all duration-300'
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className='min-w-[2.5rem] text-xs font-medium text-gray-700 dark:text-dark-text-secondary'>{progress}%</span>
          {onViewDetails && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(activeImport.taskId);
              }}
              className='rounded p-0.5 text-blue-500 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-sky-400/50'
              title={t('content.agents.workflowProgress.viewDetails')}
            >
              <EyeIcon className='h-4 w-4' />
            </button>
          )}
        </div>
      </div>
    );
  }

  // If there's a recent failed import, show error with option to view
  if (failedImport) {
    return (
      <div className='flex flex-col gap-1'>
        <div className='flex items-center gap-2'>
          <ExclamationCircleIcon className='h-4 w-4 text-red-500' />
          <span className='text-xs font-medium text-red-600 dark:text-red-400'>
            {t('content.agents.workflowProgress.failed')}
          </span>
          {onViewDetails && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(failedImport.taskId);
              }}
              className='rounded p-0.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-rose-400/50'
              title={t('content.agents.workflowProgress.viewDetails')}
            >
              <EyeIcon className='h-4 w-4' />
            </button>
          )}
        </div>
      </div>
    );
  }

  // No active import - show standard KG status badge
  if (!kg) {
    return <span className='text-xs text-gray-400 dark:text-dark-text-secondary'>{t('content.agents.kgStatus.noKG')}</span>;
  }

  const statusConfig = {
    COMPLETED: {
      icon: <CheckCircleIcon className='h-4 w-4 text-green-500' />,
      label: t('content.agents.kgStatus.ready'),
      className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    },
    PROCESSING: {
      icon: <ArrowPathIcon className='h-4 w-4 animate-spin text-blue-500' />,
      label: t('content.agents.kgStatus.building'),
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    },
    PENDING: {
      icon: null,
      label: t('content.agents.kgStatus.pending'),
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
    },
    ERROR: {
      icon: <ExclamationCircleIcon className='h-4 w-4 text-red-500' />,
      label: t('content.agents.kgStatus.error'),
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    }
  };

  const config = statusConfig[kg.status as keyof typeof statusConfig] || statusConfig.PENDING;
  const nodeCount = workflow ? ((workflow.definition as Record<string, unknown>)?.nodes as unknown[])?.length || 0 : 0;

  return (
    <div className='flex flex-col gap-1'>
      <div className='flex items-center gap-1.5'>
        {config.icon}
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}>
          {config.label}
        </span>
      </div>
      {workflow && (
        <span className='text-xs text-gray-500 dark:text-dark-text-secondary'>
          {nodeCount} {t('content.agents.workflowStatus.nodes')}
        </span>
      )}
    </div>
  );
};

export default KGAgentProgress;
